import type { SubtitleTrackRef } from './media.ts';

/**
 * R5 — playback is *described* by core and *performed* by the platform. Nothing below knows what
 * Shaka or Media3 is; the mapping table lives in docs/specs/android.md.
 */
export interface Chapter {
  readonly title: string;
  readonly startSec: number;
  readonly endSec: number;
}

export interface MediaSource_ {
  /** `?local=true` already applied — §2: direct googlevideo URLs can never work, on any platform. */
  readonly manifestUrl: string;
  /** `hls` when `liveNow || isPostLiveDvr`; `dash` otherwise. */
  readonly manifestKind: 'dash' | 'hls';
  /** itag 18, proxied. `null` when the video has no progressive stream at all (live). */
  readonly fallbackProgressiveUrl: string | null;
  readonly subtitles: readonly SubtitleTrackRef[];
  readonly storyboardVttUrl: string | null;
  readonly expiresAtEpochSec: number | null;
}

/**
 * `no-source` is an addition to R5's list, forced by a captured case (§4, fixture
 * `dvr-6aEs5K7a3Uc`): an `isPostLiveDvr` video with **no `hlsUrl` at all** whose
 * `dashUrl?local=true` returns 500. The documented `liveNow || isPostLiveDvr → hlsUrl` rule has
 * nothing to land on there, so the player needs an explicit "no source" state rather than a
 * two-URL fallback chain that silently ends in undefined.
 */
export type UnplayableReason = 'region' | 'paid' | 'private' | 'removed' | 'no-mse' | 'no-source';

export type PlaybackPlan =
  | { readonly kind: 'vod'; readonly source: MediaSource_; readonly chapters: readonly Chapter[]; readonly durationSec: number }
  | { readonly kind: 'live'; readonly source: MediaSource_ }
  | { readonly kind: 'dvr'; readonly source: MediaSource_; readonly durationSec: number }
  | { readonly kind: 'upcoming'; readonly premiereAtEpochSec: number | null }
  | { readonly kind: 'unplayable'; readonly reason: UnplayableReason };

/**
 * R5 — core models what the user *wants*; the platform resolves it against whatever tracks the
 * manifest actually offers. Never a track list: after `player.load()` every track object is a new
 * instance, so anything held by reference is dead on recovery (R6).
 */
export type VideoQualityPref =
  | { readonly kind: 'auto'; readonly maxHeight?: number; readonly codec?: CodecFamily }
  | { readonly kind: 'pinned'; readonly height: number; readonly codec?: CodecFamily };

/**
 * `drc` asks for YouTube's DRC ("Stable Volume") rendition of whichever track the rest of the
 * preference resolves to. It sits on **both** arms because it is orthogonal to the language — the
 * DRC set duplicates every language including the original — and it is optional for the same
 * reason `VideoQualityPref`'s `codec` is: absent means the ordinary track, and requiring it would
 * churn every construction site to say `drc: false`.
 *
 * Runtime-only, deliberately: the persisted choice is the `useStableVolume` setting
 * (`settings/model.ts`), stamped onto the preference when a player mounts. `normalizeAudioPref`
 * strips it from stored documents so the setting never has a second, staler source of truth.
 */
export type AudioLanguagePref =
  | { readonly kind: 'auto'; readonly drc?: boolean }
  | {
      readonly kind: 'lang';
      readonly code: string;
      readonly allowDub: boolean;
      readonly drc?: boolean;
    };

export type SubtitlePref =
  | { readonly kind: 'off' }
  | { readonly kind: 'lang'; readonly code: string; readonly allowAutoGenerated: boolean };

export interface PlaybackPreferences {
  readonly quality: VideoQualityPref;
  readonly audio: AudioLanguagePref;
  readonly subtitle: SubtitlePref;
  readonly playbackRate: number;
}

export const DEFAULT_PLAYBACK_PREFERENCES: PlaybackPreferences = {
  quality: { kind: 'auto' },
  // Never auto-select audio by browser locale: dubs are often worse than the original, and
  // `Accept-Language` is not consent (§7).
  audio: { kind: 'auto' },
  subtitle: { kind: 'off' },
  playbackRate: 1,
};

/**
 * §2/§3 — the ladder carries H.264 and AV1, so "1080p" exists twice and quality must be pinned by
 * (height, codec family) rather than by a flat quality list.
 *
 * `codec` sits on **both** arms of {@link VideoQualityPref}, which the sketch in §5 did not have.
 * §7 asks for "Force H.264" for old hardware and the TV target, and forcing a codec is orthogonal
 * to choosing a height: with it only on the pinned arm, turning H.264 on would also have to freeze
 * the resolution, so a user on a slow connection would be choosing between a codec their hardware
 * can decode and an adaptive ladder. This needs the same pair.
 */
export type CodecFamily = 'avc' | 'av1' | 'vp9' | 'other';

export function codecFamilyOf(codec: string): CodecFamily {
  const value = codec.trim().toLowerCase();
  if (value.startsWith('avc') || value.startsWith('h264')) return 'avc';
  if (value.startsWith('av01') || value.startsWith('av1')) return 'av1';
  if (value.startsWith('vp9') || value.startsWith('vp09')) return 'vp9';
  return 'other';
}

/**
 * R6 — recovery re-selects by **stable key**, never by object identity. `${height}@${family}` for
 * video; `language` plus sorted roles for audio.
 */
export function videoTrackKey(height: number, codec: string): string {
  return `${height}@${codecFamilyOf(codec)}`;
}

export function audioTrackKey(language: string, roles: readonly string[]): string {
  return `${language.toLowerCase()}#${[...roles].map((r) => r.toLowerCase()).sort().join(',')}`;
}
